// Cookie banner — shown once on first visit, dismissible with "Got it".
// Honest scope: PX7 Training's marketing site only uses essential cookies (the app's
// sign-in cookie, loaded only inside app.px7.training), but this banner
// still surfaces because EU visitors expect to see one, and the `/cookies`
// page has more detail for anyone who wants the full story.
//
// This notice links to the cookie page, including PX7 Digital analytics and Google Fonts.
// It is an informational notice, not a consent-management implementation.
//
// Dismissal persists in localStorage so repeat visitors don't re-see it.
// Keyed with a version suffix so bumping policy text can force re-display.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "px7.cookies.ack.v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  // Defer visibility to an effect so SSR/static-render doesn't flash the
  // banner before localStorage has been read. If the user has already
  // acknowledged, it never renders.
  useEffect(() => {
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      // localStorage blocked (private mode on old Safari). Show once per
      // session so the user can still dismiss it — it just won't persist.
    }
    setVisible(true);
  }, []);

  function dismiss() {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch { /* ignore */ }
    setVisible(false);
  }

  if (!visible) return null;
  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      className="fixed bottom-0 inset-x-0 z-50 p-3 safe-bottom"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-px-line bg-white/95 backdrop-blur-sm shadow-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-xs sm:text-sm text-px-text flex-1 leading-relaxed">
          PX7 Training uses one essential sign-in cookie inside the app, and
          loads Stripe + Turnstile on the specific pages that need them.
          The site also loads PX7 Digital analytics and Google Fonts.{" "}
          <Link to="/cookies" className="text-px-green-dark underline underline-offset-2">
            Details
          </Link>.
        </p>
        <button
          type="button"
          onClick={dismiss}
          className="mk-button-primary text-xs whitespace-nowrap"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
